import {
  ChatAlt2Icon,
  PhotographIcon,
  TranslateIcon,
  VideoCameraIcon,
} from '@heroicons/react/outline';
import useLocale from 'next-translate/useTranslation';

import ButtonLink from '@/components/links/ButtonLink';

/**
 * Landing-page section cross-selling the AI Talking Avatars feature on the
 * TTS language/accent/style pages — same voices, but with a face. The demo
 * clip is one of the real examples from /ai-video-generator/talking.
 */
export default function TalkingAvatarsSection({
  languageName,
  isAccent = false,
  isStyle = false,
}: {
  languageName: string;
  isAccent?: boolean;
  isStyle?: boolean;
}) {
  const { t } = useLocale('text-to-speech');

  // Full-sentence keys with a {{language}}/{{accent}} placeholder (the
  // HowToCreateSection convention) — never concatenated fragments, which
  // can't survive translation word order. Style pages (funny, scary, ...)
  // have no language to name, so they get their own generic headline.
  const headline = isStyle
    ? t('Give your voiceover a face')
    : isAccent
      ? t('Give your {{accent}} accent voice a face', {
          accent: t(languageName),
        })
      : t('Give your {{language}} voice a face', { language: t(languageName) });

  const features = [
    {
      icon: PhotographIcon,
      title: t('Any photo becomes a presenter'),
      body: t(
        'Upload a portrait or generate one with AI — our avatars speak with realistic lip-sync and natural expressions.',
      ),
    },
    {
      icon: ChatAlt2Icon,
      title: t('Powered by the same voices'),
      body: t(
        'Pick any voice from this page — or a clone of your own — and your avatar speaks with it.',
      ),
    },
    {
      icon: TranslateIcon,
      title: t('40+ languages and accents'),
      body: t(
        'Localize one presenter into every market without re-shooting a single frame.',
      ),
    },
  ];

  return (
    <div className='bg-white py-24 dark:bg-slate-950 sm:py-32'>
      <div className='mx-auto max-w-7xl px-6 lg:px-8'>
        <div className='grid gap-12 lg:grid-cols-2 lg:items-center lg:gap-16'>
          {/* Demo videos — the two hero examples from /ai-video-generator/talking */}
          <div className='order-last flex justify-center gap-4 lg:order-first'>
            {[
              {
                src: 'https://videos.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/talking-video_final_b4cc830f-c5ca-435e-8da0-05a0049898aa.mp4',
                label: t('Person Speaking'),
              },
              {
                src: 'https://videos.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/talking-video_final_b5ef5f5c-cd31-42ef-864b-ddbc3a867a84.mp4',
                label: t('AI Avatar'),
              },
            ].map((video) => (
              <div
                key={video.src}
                className='relative w-full max-w-[260px] overflow-hidden rounded-2xl border border-primary-200/40 shadow-2xl shadow-primary-500/10 dark:border-primary-800/30'
              >
                <div className='relative aspect-[9/16] bg-gray-100 dark:bg-slate-800'>
                  <video
                    autoPlay
                    muted
                    loop
                    playsInline
                    className='h-full w-full object-cover'
                    src={video.src}
                  />
                </div>
                <div className='absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent px-3 pb-3 pt-8'>
                  <span className='text-xs font-medium text-white/90'>
                    {video.label}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* Copy */}
          <div>
            <h2 className='text-base font-semibold leading-7 text-primary-600 dark:text-primary-400'>
              {t('Talking Avatars')}
            </h2>
            <p className='mt-2 text-3xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-4xl'>
              {headline}
            </p>
            <p className='mt-6 text-lg leading-8 text-gray-600 dark:text-slate-400'>
              {t(
                'Turn any script into a video of a lifelike AI presenter. Type your text, choose a voice and a photo, and get a ready-to-share talking avatar video in minutes.',
              )}
            </p>
            <ul className='mt-8 space-y-5'>
              {features.map((feature, i) => (
                <li key={i} className='flex gap-4'>
                  <span className='flex h-9 w-9 flex-none items-center justify-center rounded-lg bg-primary-50 dark:bg-primary-500/10'>
                    <feature.icon
                      className='h-5 w-5 text-primary-600 dark:text-primary-400'
                      aria-hidden='true'
                    />
                  </span>
                  <div>
                    <p className='font-semibold text-gray-900 dark:text-white'>
                      {feature.title}
                    </p>
                    <p className='mt-0.5 text-sm leading-6 text-gray-600 dark:text-slate-400'>
                      {feature.body}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
            <div className='mt-10'>
              <ButtonLink href='/ai-talking-videos' variant='dark'>
                <VideoCameraIcon className='mr-2 h-5 w-5' aria-hidden='true' />
                {t('Create a Talking Avatar')}
              </ButtonLink>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
